import React, { useState } from "react";
import TextareaAutosize from "react-autosize-textarea";
import { MessageTemplate, Page } from "../../types";
import Select from "react-select";
import PhoneNumberDisplay from "../../../components/PhoneNumberDisplay";
import { Organization } from "../../organizations/types";
import { Patient } from "../../patient/types";
import { Link } from "react-router-dom";
import UploadAttachment from "../../../components/UploadAttachment";
import LoadingFullScreen from "../../../components/LoadingFullScreen";

interface OwnProps {
  patient: Patient | null;
  messageTemplateList: Page<MessageTemplate> | null;
  currentOrg: Organization | null;
  sendUnsecureMessage: (patient: Patient, message: string) => void;
  sendSecureMessage: (patient: Patient, message?: string, attachments?: string[]) => void;
  flash: (body: string, level: "error") => void;
}

interface OwnState {
  submitDisabled: boolean;
  message: string;
  phoneNumber: string;
  attachments: string[];
  loading: boolean;
}

type Props = OwnProps;

class MessagePatient extends React.PureComponent<Props, OwnState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      submitDisabled: true,
      message: "",
      phoneNumber: this.props.patient && this.props.patient.phone ? this.props.patient.phone : "",
      attachments: [],
      loading: false
    };

    this.messageChanged = this.messageChanged.bind(this);
    this.setLoading = this.setLoading.bind(this);
    this.sendMessage = this.sendMessage.bind(this);

    this.validatePhoneNumber();
  }

  public setLoading = (isLoading: boolean) => {
    this.setState({ loading: isLoading });
  };

  private validatePhoneNumber = () => {
    let ret = false;
    if (this.state.phoneNumber.replace(/[^0-9]/g, "").length == 10) {
      ret = true;
    }
    if (!ret)
      this.props.flash(
        "The patient phone number is invalid. Please correct this on the patient details screen.",
        "error"
      );

    return ret;
  };

  private messageChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ message: e.target.value }, () => this.validate());
  };

  public sendMessage = () => {
    if (this.props.patient) {
      if (this.props.messageTemplateList?.results.find(x => x.content === this.state.message)) {
        this.props.sendUnsecureMessage(this.props.patient, this.state.message);
      } else {
        this.props.sendSecureMessage(
          this.props.patient,
          this.state.message,
          this.state.attachments
        );
      }
    }
  };

  private libraryOptions = () => {
    return this.props.messageTemplateList?.results.map(template => {
      return { value: template.content, label: template.friendlyName };
    });
  };

  private handleSelectedLocationChange = (e: { value: string; label: string }) => {
    if (e) {
      this.setState({ message: e.value }, () => this.validate());
    } else {
      this.setState({ message: "" }, () => this.validate());
    }
  };

  private validate = () => {
    this.setState({
      submitDisabled: !(
        (this.state.message.trim() || this.state.attachments.length > 0) &&
        this.validatePhoneNumber()
      )
    });
  };

  private canNotSendSecure = (): boolean => {
    return !!(
      this.props.patient &&
      !this.props.patient.dateOfBirth?.isValid() &&
      this.state.message &&
      this.props.messageTemplateList?.results.findIndex(x => x.content === this.state.message) ===
        -1
    );
  };

  private setAttachment = (attachmentId: any) => {
    this.setState(
      { attachments: this.state.attachments.concat(attachmentId), loading: false },
      () => this.validate()
    );
  };

  render() {
    return (
      <>
        <div className="message-container sections">
          <div className="section p-15">
            <h1>Send Patient Message</h1>
            <div className="title mb-20">
              Patient: {this.props.patient?.givenName} {this.props.patient?.familyName}
            </div>

            <label htmlFor="phone">Phone Number</label>
            <div className="mt-10">
              <PhoneNumberDisplay phonenumber={this.state.phoneNumber} />
            </div>

            <div className="flex-between-bottom mt-20">
              <label htmlFor="sms-message">Message:</label>

              <Select
                className="location-select"
                placeholder="Message Library"
                onChange={this.handleSelectedLocationChange}
                options={this.libraryOptions()}
                isClearable={true}
              />
            </div>

            <TextareaAutosize
              id="sms-message"
              className="form-control text-area mt-10"
              value={this.state.message}
              onChange={this.messageChanged}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {this.canNotSendSecure() ? (
              <div className="alert alert-error mt-15">
                In order to send a secure message the patient must have a date of birth entered.
                Please select a library message or go{" "}
                <Link className="hyperlink" to={"/patient/" + this.props.patient?.id + "/edit/"}>
                  here
                </Link>{" "}
                to put in the missing data.
              </div>
            ) : (
              <div className="mt-15 flex-between">
                <div>
                  {this.state.loading && <LoadingFullScreen />}
                  <UploadAttachment
                    addAttachment={this.setAttachment}
                    setLoading={this.setLoading}
                  />
                  {this.state.attachments.length > 0 && (
                    <span className="bold">Attachment added.</span>
                  )}
                </div>
                <button
                  disabled={this.state.submitDisabled}
                  className={!this.state.submitDisabled ? "highlight" : ""}
                  onClick={this.sendMessage}
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default MessagePatient;
